import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";

function translateError(message: string): string {
  const msg = message.toLowerCase();
  if (msg.includes("rate limit") || msg.includes("too many")) return "发送过于频繁，请稍后再试";
  if (msg.includes("unable to validate email") || msg.includes("invalid")) return "邮箱格式不正确，请检查后重试";
  if (msg.includes("password") && msg.includes("at least")) return "密码至少需要 6 位";
  return message;
}

interface UseResetPasswordResult {
  sending: boolean;
  saving: boolean;
  error: string | null;
  requestReset: (email: string) => Promise<boolean>;
  updatePassword: (password: string) => Promise<boolean>;
  clearError: () => void;
}

export function useResetPassword(): UseResetPasswordResult {
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const requestReset = useCallback(async (email: string): Promise<boolean> => {
    setSending(true);
    setError(null);
    try {
      const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
      const pathPrefix = basePath ? `/${basePath}` : "";
      const redirectTo = `${window.location.origin}${pathPrefix}/reset-password`;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (resetError) throw resetError;
      return true;
    } catch (err) {
      setError(translateError(err instanceof Error ? err.message : "发送失败，请稍后重试"));
      return false;
    } finally {
      setSending(false);
    }
  }, []);

  const updatePassword = useCallback(async (password: string): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      return true;
    } catch (err) {
      setError(translateError(err instanceof Error ? err.message : "密码更新失败，请稍后重试"));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { sending, saving, error, requestReset, updatePassword, clearError };
}